﻿import React, {
  createContext,
  Dispatch,
  MutableRefObject,
  ReactNode,
  SetStateAction,
  useContext,
  useRef,
  useState
} from 'react';
import { useMediaQuery } from '@SharedScripts/Hooks/useMediaQuery';
import { isDesktop } from '@SharedScripts/Utils/DeviceClass';

type SliderContextProps = {
  index: MutableRefObject<number>;
  position: MutableRefObject<number>;
  groupSize: MutableRefObject<number>;
  slideAmount: MutableRefObject<number>;
  tileWidth: number;
  setTileWidth: (width: number) => void;
  forceUpdate: number;
  setForceUpdate: Dispatch<SetStateAction<number>>;
  parentRect: MutableRefObject<DOMRect | null>;
  tiles: MutableRefObject<Array<HTMLDivElement | null>>;
  root: MutableRefObject<HTMLDivElement | null>;
  parentRef: MutableRefObject<HTMLElement | null>;
  prefersReducedMotion: boolean;
  useMobileCssSlider: boolean;
}

const SliderContext = createContext<SliderContextProps | undefined>(undefined);

export const SliderProvider = ({ children }: {children: ReactNode}) => {
  const root = useRef<HTMLDivElement | null>(null);
  const index = useRef(0);
  const position = useRef(0);
  const groupSize = useRef(1);
  const slideAmount = useRef(1);
  const parentRect = useRef<DOMRect | null>(null);
  const parentRef = useRef<HTMLElement | null>(null);
  const tiles = useRef<Array<HTMLDivElement | null>>([]);
  const [tileWidth, setTileWidth] = useState(0);
  const [forceUpdate, setForceUpdate] = useState(0);
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const useMobileCssSlider = !isDesktop();

  return (
    <SliderContext.Provider
      value={{
        root,
        parentRef,
        index,
        position,
        groupSize,
        slideAmount,
        tileWidth,
        setTileWidth,
        forceUpdate,
        setForceUpdate,
        parentRect,
        tiles,
        prefersReducedMotion,
        useMobileCssSlider
      }}
    >
      {children}
    </SliderContext.Provider>
  );
};

export const useSliderContext = () => {
  const context = useContext(SliderContext);
  if (!context) {
    throw new Error('useSliderContext must be used within a SliderProvider');
  }
  return context;
};
